/**
 * Every author-facing string for Author with AI, in one place. Estimates are
 * provisional: each says "usually" and is corrected from the live
 * walkthrough's observed turn and build durations.
 */

import type { BriefField, DecisionKey } from '../../api/authorWithAiApi';

/** Entry points whose backend path is not delivered render disabled with NOT_AVAILABLE_LABEL. */
export const ENTRY_POINT_AVAILABILITY = {
  question: true,
  video: true,
  customize: false,
  upload: true,
} as const;

export const NOT_AVAILABLE_LABEL = 'Not available yet';

export const LANDING_COPY = {
  heading: 'What do you want to build?',
  expertiseHeading: "Start with Emtrain's expertise",
  ownHeading: 'Start on your own',
  or: 'or',
  askPlaceholder: 'Describe a lesson or course to build...',
  askLabel: 'Describe a lesson or course to build',
  examplePrefix: 'Ex:',
  anotherExample: 'Show another example',
  send: 'Start building with AI',
  uploadChip: 'Upload a file',
  uploadNote: 'a deck, document, or script to build from',
  uploadReading: (fileName: string) => `Reading ${fileName}...`,
  uploadUploading: (fileName: string) => `Uploading ${fileName}...`,
  uploadFailed: 'Could not upload the file.',
  notReady: (settings: string[]) => (settings.length > 0
    ? `Author with AI is not set up on this server yet. Missing settings: ${settings.join(', ')}.`
    : 'Author with AI is not set up on this server yet.'),
  inProgress: 'In progress',
  recent: 'Recent',
  conversations: 'Conversations',
  showMore: 'Show more',
  emptyRecent: 'Nothing built yet.',
  emptyConversations: 'No conversations yet.',
  listError: 'Could not load your builds.',
} as const;

export const EXPERTISE_CARDS = [
  {
    id: 'question',
    title: 'Ask a question',
    body: "Choose from Emtrain's bank of behavioral questions. Answers drive benchmarked risk and culture data.",
  },
  {
    id: 'video',
    title: 'Lead with a video scenario',
    body: 'Build a lesson around a scenario from Centerstage, our library of real workplace experiences that employees recognize.',
  },
  {
    id: 'customize',
    title: 'Customize a course',
    body: 'Adapt a standard Emtrain course to your organization with help from AI. Legally required content stays protected.',
  },
] as const;

export const ASK_EXAMPLES = [
  'A refresher on our code of conduct',
  'Manager training on handling complaints',
  'A short lesson on data privacy',
  'Bystander intervention for retail staff',
  'What to do when a complaint involves your boss',
] as const;

export const ORIGIN_LABELS = {
  none: 'Started from a chat',
  question: 'Started from a bank question',
  // "Ask a question" builds share the question glyph and label.
  question_browse: 'Started from a bank question',
  video: 'Started from a video scenario',
  // "Lead with a video scenario" builds share the video glyph and label.
  video_browse: 'Started from a video scenario',
} as const;

/** The file glyph on a list row downloads the original file. */
export const ORIGIN_FILE_LABEL = (fileName: string) => `Started from ${fileName}. Download the file.`;

export const BADGES = {
  building: 'Building',
  draft: 'Draft',
  published: 'Published',
  unfinished: 'Unfinished',
  failed: 'Build failed',
  completed: 'Completed',
  discarded: 'Discarded',
} as const;

export const UNAVAILABLE_COPY = {
  heading: 'Author with AI is not available',
  body: 'This server does not have Author with AI turned on.',
} as const;

export const CONVERSATION_COPY = {
  back: '← Author with AI',
  notFoundHeading: 'Build not found',
  notFoundBody: 'This build does not exist or belongs to someone else.',
  notFoundLink: 'Back to Author with AI',
  loadError: 'Could not load this build.',
  menuLabel: 'Build options',
  discard: 'Discard this build',
  discardTitle: 'Discard this build?',
  discardBody: 'The conversation is removed from your lists. Lessons and courses already saved stay where they are.',
  discardConfirm: 'Discard',
  cancel: 'Cancel',
  you: 'You',
  assistant: 'Author with AI',
} as const;

export const COMPOSER_COPY = {
  placeholderWithChips: 'Or reply directly...',
  placeholder: 'Reply...',
  label: 'Reply to Author with AI',
  send: 'Send',
  turnActive: 'Still working on the last step',
  building: 'Building. You can leave and come back.',
  completed: 'This conversation is finished. Open the result in the editor.',
  failed: 'The build stopped. Use the options above to retry, revise or discard it.',
  discarded: 'This build was discarded',
  busy: 'Still working on the last step',
  conflict: 'Something changed while you were typing. Try again.',
} as const;

/** One sentence per planning stage; no invented progress lines. */
export const WAITING_COPY = {
  browse: 'Looking through the question bank. Usually under a minute.',
  browseVideo: 'Looking through the scenario library. Usually under a minute.',
  reading: (unitCount: number, unit: string) => `Reading it now. About a minute for ${unitCount} ${unit}.`,
  outline: 'Working on your outline. Usually under a minute.',
  blocks: 'Breaking each lesson into blocks. Usually a minute or two.',
  generic: 'Working on it.',
  elapsedAfterMs: 30_000,
} as const;

export const GENERATION_COPY = {
  lesson: 'Building it now. This usually takes a few minutes.',
  course: 'Building it now: the lessons and the course that holds them. This usually takes five to ten minutes.',
  stayOrLeave: "You can stay here and I'll post the link when it's ready, or find it later under Recent on the Author with AI dashboard.",
  courseRow: 'Course',
} as const;

/** Build stages the API reports (generation checkpoint stage). */
export const BUILD_STAGE_LABELS: Readonly<Record<string, string>> = {
  prepare_plan: 'Planning blocks',
  preflight_references: 'Checking the selected questions and videos',
  lessons: 'Building lessons',
  course: 'Creating the course',
  done: 'Finishing up',
};

/** Lesson stages the API reports while a lesson is running. */
export const LESSON_STAGE_LABELS: Readonly<Record<string, string>> = {
  content: 'Drafting content',
  images: 'Creating images',
  create: 'Saving the lesson',
  blocks: 'Saving the lesson',
  shell: 'Registering the lesson',
};

export const LESSON_STATUS_LABELS = {
  pending: 'Waiting',
  ready: 'Ready',
  failed: 'Failed',
} as const;

export const UNKNOWN_STAGE_LABEL = 'Working';

export const RESULT_COPY = {
  heading: 'Your drafts are ready',
  headingOne: 'Your draft is ready',
  openInEditor: 'Open in editor',
  partial: '(partial)',
  earlierDrafts: 'Earlier drafts',
  course: 'Course',
  lesson: 'Lesson',
  accountNote: (account: string) =>
    `These drafts open read-only in ${account}'s content context. Edit them with the content context set to "Emtrain (Standard)" until they are published.`,
} as const;

export const FAILURE_COPY = {
  turnFailed: 'That step did not finish.',
  retry: 'Retry',
  retryReply: 'Retry the reply',
  buildFailed: 'The build stopped before it finished.',
  stage: (label: string) => `Stopped at: ${label}`,
  saved: 'Saved so far:',
  revise: 'Revise',
  discard: 'Discard',
  needsInput: 'The build needs your input before it can continue:',
} as const;

export const OUTLINE_COPY = {
  lessonOf: (position: number, total: number) => `Lesson ${position} of ${total}`,
  wrapUp: 'Wrap-up',
  lessonComplete: 'Lesson Complete',
  aboutMinutes: (minutes: number) => `About ${minutes} minutes`,
  minutesShort: (minutes: number) => `${minutes} min`,
  /** "slides 1–9" after the minutes of a lesson built from an uploaded file. */
  sourceRange: (unit: string, range: string) => `${unit} ${range}`,
  endToEnd: (minutes: number) => `About ${minutes} minutes end to end`,
  removed: (purpose: string) => `Removed: ${purpose}`,
  added: 'New',
  retyped: 'Changed',
  approved: 'Approved',
  assumed: (fields: string[]) => `Assumed: ${fields.join(', ')}`,
  needsApproval: (items: string[]) => `Needs your approval: ${items.join(', ')}`,
  lessonList: 'the lesson list',
  staleRevision: 'The outline changed. Review the latest version.',
  noLongerAvailable: '(no longer available)',
} as const;

export const BRIEF_FIELD_LABELS: Readonly<Record<BriefField, string>> = {
  topic: 'topic',
  audience: 'audience',
  objectives: 'objectives',
  shape: 'lesson or course',
  targetMinutes: 'target length',
};

/** Fixed chips derived from the build state (model suggestions render before them). */
export const CHIP_LABELS = {
  addAllThree: 'Add all three',
  addBoth: 'Add both',
  addIt: 'Add it',
  justFirst: 'Just the first',
  neither: 'Neither',
  noThanks: 'No thanks',
  useIt: 'Use it',
  showAnother: 'Show another',
  skipVideo: 'Skip the video',
  skipQuestions: 'Skip the questions',
  skipQuestion: 'Skip the question',
  chooseOwn: 'Let me choose my own',
  looksGood: 'Looks good',
  reviewByLesson: 'Review lesson by lesson',
  skipReview: "Skip it, I'll review in the editor",
  reviewListAgain: 'Review the lesson list again',
  backToLesson: 'Go back to a lesson',
  /** The primary chip; the only control that submits the start action. */
  start: 'Start building',
  // Rendered as a link after the chips ("or search all questions"), not as a chip.
  searchBank: 'search all questions',
  searchCenterstage: 'search all scenarios',
} as const;

/** Leads the search link when chips sit before it. */
export const SEARCH_LINK_LEAD = 'or';

export const DECISION_COPY: Readonly<Record<DecisionKey, { choose: string; picker: string; multi: boolean }>> = {
  anchor_question: { choose: 'Choose a question', picker: 'Choose a question', multi: false },
  additional_questions: { choose: 'Choose questions', picker: 'Choose questions', multi: true },
  video: { choose: 'Choose a video', picker: 'Choose a video', multi: false },
};

/** Skip chip per optional pending decision. */
export const SKIP_CHIP_LABELS: Readonly<Record<DecisionKey, string>> = {
  anchor_question: CHIP_LABELS.skipQuestion,
  additional_questions: CHIP_LABELS.skipQuestions,
  video: CHIP_LABELS.skipVideo,
};

export const SELECTION_COPY = {
  skipped: (decision: DecisionKey) => ({
    anchor_question: 'No anchor question',
    additional_questions: 'No additional questions',
    video: 'No video',
  })[decision],
  approvedLesson: (position: number | null) => (position ? `Approved lesson ${position}` : 'Approved the lesson'),
  approvedCourse: (mode: 'detailed' | 'delegated') => (mode === 'detailed'
    ? 'Approved the lesson list. Review lesson by lesson.'
    : "Approved the lesson list. I'll review the blocks in the editor."),
  questionFallback: (id: string) => `Question Q${id}`,
  videoFallback: (id: string) => `Video ${id}`,
} as const;

export const PICKER_COPY = {
  close: 'Close',
  showAll: (count: number) => `Show all ${count}`,
  scrollForMore: 'Scroll for more',
  usedIn: (count: number) => `Used in ${count} ${count === 1 ? 'lesson' : 'lessons'}`,
  videoPickerTitle: 'Centerstage',
  videoPickerDescription: 'Browse real-life workplace video scenarios, then add one to your lesson.',
  videoEmptyTitle: 'No videos match that search.',
  videoEmptyHint: 'Try a different word, or clear the concept to see more.',
  select: 'Select',
  selectVideo: (title: string) => `Select ${title}`,
  play: (title: string) => `Preview ${title}`,
  useThisOne: 'Use this one',
  loadError: 'Could not load the list.',
} as const;

/** The question type names, shared with the Question Bank browse surfaces. */
export { QUESTION_BANK_TYPE_LABELS as QUESTION_TYPE_LABELS } from '../questionBankBrowse/questionBankBrowseCopy';

/**
 * UI labels for outline rows over the planner's block types (not the
 * editor's block names). Wrap-up is the Lesson Complete row the core adds.
 */
export const BLOCK_DISPLAY_NAMES: Readonly<Record<string, string>> = {
  'basic-content-a': 'Basic Content',
  'stylized-text-callout': 'Text Callout',
  'stylized-numbered-list': 'Numbered List',
  'stylized-sidebar-callout': 'Sidebar',
  'comparison-slider': 'Comparison Slider',
  'click-expand': 'Click-Expand',
  video: 'Video',
  'single-choice-answer': 'Single Choice',
  'likert-scale': 'Likert Scale',
  'workplace-color-spectrum': 'Workplace Color Spectrum',
  categorization: 'Categorization',
};
